import React, { useState, useEffect, useRef, useContext } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import InfoPopUp from "../../Common/InfoPopUp";
import { DataContext } from ".";
import InfoIcon from "@mui/icons-material/Info";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import * as Stomp from "stompjs";
import * as SockJS from "sockjs-client";

const currencySymbols = {
  INR: "₹",
  USD: "$",
  USDT: "$",
  AED: "د.إ",
  EUR: "€",
  SGD: "S$",
};

const title = "Orderbook";
const description =
  "Sell offers are displayed on top in red color, and Buy offers are displayed at the bottom in green color. The right side of the order book is displaying the current 50 trades that took place in the market in the last 24 hours.";

const OrderBook = () => {
  const {
    chartData,
    setFinalBiddata,
    setFinalAskdata,
    currencycode,
    basecurrency,
    lowPrice,
    highPrice,
    setSellData,
    setBuyData,
    setLastAskItem,
    setFirstBidItem,
    lastAskItem,
    firstBidItem,
  } = useContext(DataContext);

  const tableContentRef = useRef(null);
  const websocketRef = useRef({ socket: null, subscribeMessage: null });
  const theme = localStorage.getItem("theme");
  const { brokerBasicInfo } = useSelector((state) => state);

  const [biddata, setBiddata] = useState([]);
  const [askdata, setAskdata] = useState([]);
  const [tradep, setTradep] = useState(6);
  const [token, setToken] = useState(null);
  const [show, setShow] = useState(false);
  const [toggle, setToggle] = useState(true);

  const textColor = "#f1b900";
  const containerClass = "sp-highlow";

  const webSocketEndPoint = "https://stream.paybito.com/SocketStream/ws";
  const topic = "/user/topic/stream";
  const topicForBtcUsd = "/topic/stream/BTCUSD";

  let stompClient = null;
  let reqNo = null;

  const handleClose = () => setShow(false);

  // ------------------- UTILITY FUNCTIONS -------------------


  const hasValidP = (value) => {
    // null / undefined / false
    if (value === null || value === undefined || value === false) return false;

    const str = String(value).trim().toLowerCase();

    // blank / "undefined" / "null"
    return str !== "" && str !== "undefined" && str !== "null";
  };




  const getPrice = (m) => {
    const prc = localStorage.getItem("priceprc");
    //console.log('price precision ->', hasValidP(prc))
    const p = parseInt(hasValidP(prc) ? prc : 6);
    return parseFloat(m.price).toFixed(p);
  };

  const getAmount = (m) => {
    const prc = localStorage.getItem("amountprc");
    //console.log('amount precision ->', hasValidP(prc))
    const s = hasValidP(prc) ? prc : 6;
    return parseFloat(m.amount).toFixed(s);
  };

  const handleSellClick = (data) => setSellData(data);
  const handleBuyClick = (data) => setBuyData(data);

  const formatNumber = (num) => {
    const strNum = num.toString();
    const [integerPart, decimalPart = ""] = strNum.split(".");
    return decimalPart.length < 6
      ? `${integerPart}.${decimalPart.padEnd(6, "0")}`
      : strNum;
  };

  // ------------------- WEBSOCKET FUNCTIONS -------------------

  const connectWebSocket = () => {
    const accessToken = localStorage.getItem("access_token");
    if (!accessToken) return;

    const ws = new SockJS(webSocketEndPoint);
    stompClient = Stomp.over(ws);

    stompClient.connect(
      {},
      () => {
        websocketRef.current.socket = stompClient;

        let topicToSubscribe =
          currencycode?.toLowerCase() === "btc" &&
          basecurrency?.toLowerCase() === "usd"
            ? topicForBtcUsd
            : topic;

        stompClient.subscribe(topicToSubscribe, (sdkEvent) => {
          const message = JSON.parse(sdkEvent.body);
          websocketRef.current.subscribeMessage?.(message);
          onMessageReceived(sdkEvent);
        });

        subscribe();
      },
      errorCallback
    );
  };

  const errorCallback = (error) => {
    console.error("WebSocket Error:", error);
    if (localStorage.getItem("access_token")) {
      setTimeout(connectWebSocket, 5000);
    }
  };

  const sendMessage = (message) => {
    if (!stompClient) return;

    let sendEndpoint =
      currencycode === "btc" && basecurrency === "usd"
        ? "/app/sendRequest/BTCUSD"
        : "/app/sendRequest";

    stompClient.send(sendEndpoint, {}, JSON.stringify(message));
  };

  const onMessageReceived = (message) => {
    const parsedMessage = JSON.parse(message.body);
    // Optional: handle parsedMessage if needed
  };

  const subscribe = () => {
    if (!currencycode || !basecurrency) return;
    reqNo = Math.floor(Math.random() * 1000) + 1;
    const req = {
      method: "SUBSCRIBE",
      params: [
        `${currencycode}/${basecurrency}@ticker`,
        `${currencycode}${basecurrency}@depth`,
        `${currencycode}${basecurrency}@trade`,
      ],
      id: reqNo,
    };
    setTimeout(() => sendMessage(req), 10000);
    localStorage.setItem("isUnsubscribeOccured", "false");
  };

  const disconnectWebSocket = () => {
    if (stompClient?.connected) {
      stompClient.disconnect(() => {
        websocketRef.current.socket = null;
        console.log("WebSocket Disconnected");
      });
    }
  };

  // ------------------- API FUNCTIONS -------------------

  const serverSentEventForOrderbookAsk = async () => {
    try {
      const response = await axios.get(
        `https://stream.paybito.com/SocketStream/api/depth?symbol=${currencycode}${basecurrency}&limit=150`,
        {
          headers: {
            "Content-Type": "application/json",
            authorization: `BEARER ${token}`,
          },
        }
      );

      const result = response.data;
      const askSideData = [...result.ask].sort((a, b) => b.price - a.price);
      const bidSideData = [...result.bid];
      setAskdata(askSideData);
      setBiddata(bidSideData);

      websocketRef.current.subscribeMessage = (orderbookData) => {
        if (orderbookData.e === "depthUpdate") {
          const prc = localStorage.getItem("priceprc");
          const currentTradeP = parseInt(hasValidP(prc) ? prc : 6);

          // ASK UPDATE
          if (orderbookData.ask) {
            setAskdata((prev) => {
              const updated = [...prev];
              orderbookData.ask.forEach((item) => {
                const priceFixed = parseFloat(item.price).toFixed(currentTradeP);
                const index = updated.findIndex(
                  (x) => parseFloat(x.price).toFixed(currentTradeP) === priceFixed
                );
                if (item.action === "NEW") {
                  updated.push({
                    price: priceFixed,
                    amount: parseFloat(item.quantity).toFixed(currentTradeP),
                    newly: true,
                  });
                } else if (item.action === "CHANGE" && index > -1) {
                  updated[index] = {
                    ...updated[index],
                    amount: parseFloat(item.quantity).toFixed(currentTradeP),
                    newly: true,
                  };
                } else if (
                  (item.action === "DELETE" || item.action === "TRADEDELETE") &&
                  index > -1
                ) {
                  updated.splice(index, 1);
                }
              });
              return updated.sort((a, b) => b.price - a.price).slice(-150);
            });
          }

          // BID UPDATE
          if (orderbookData.bid) {
            setBiddata((prev) => {
              const updated = [...prev];
              orderbookData.bid.forEach((item) => {
                const priceFixed = parseFloat(item.price).toFixed(currentTradeP);
                const index = updated.findIndex(
                  (x) => parseFloat(x.price).toFixed(currentTradeP) === priceFixed
                );
                if (item.action === "NEW") {
                  updated.push({
                    price: priceFixed,
                    amount: parseFloat(item.quantity).toFixed(currentTradeP),
                    newly: true,
                  });
                } else if (item.action === "CHANGE" && index > -1) {
                  updated[index] = {
                    ...updated[index],
                    amount: parseFloat(item.quantity).toFixed(currentTradeP),
                    newly: true,
                  };
                } else if (
                  (item.action === "DELETE" || item.action === "TRADEDELETE") &&
                  index > -1
                ) {
                  updated.splice(index, 1);
                }
              });
              return updated.sort((a, b) => b.price - a.price).slice(0, 150);
            });
          }
        }
      };

      connectWebSocket();
    } catch (error) {
      console.error("Error fetching order book data:", error);
    }
  };

  // ------------------- EFFECTS -------------------

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    setToken(token);
    serverSentEventForOrderbookAsk();

    return () => disconnectWebSocket();
  }, [currencycode, basecurrency]);

  useEffect(() => {
    if (tableContentRef.current) {
      tableContentRef.current.scrollTop = tableContentRef.current.scrollHeight;
    }
  }, [askdata]);

  useEffect(() => {
    setFinalBiddata(biddata);
    setFinalAskdata(askdata);
  }, [biddata, askdata, setFinalBiddata, setFinalAskdata]);

  useEffect(() => {
    if (askdata && askdata.length > 0) {
      const sortedAsk = [...askdata].sort((a, b) => b.price - a.price);
      setLastAskItem(sortedAsk[sortedAsk.length - 1]);
    }
  }, [askdata]);

  useEffect(() => {
    if (biddata && biddata.length > 0) {
      const sortedBid = [...biddata].sort((a, b) => b.price - a.price);
      setFirstBidItem(sortedBid[0]);
    }
  }, [biddata]);

  useEffect(() => {
    if (lastAskItem) {
      console.log("last item of askdata after sorting", lastAskItem);
    }
  }, [lastAskItem]);

  useEffect(() => {
    if (firstBidItem) {
      console.log("first item of biddata after sorting", firstBidItem);
    }
  }, [firstBidItem]);

  // ------------------- RENDER -------------------

  return (
    <div style={{ marginRight: "2px", marginTop: "11px" }}>
      {/* TABLE HEADER */}
      <div
        className="table_content tour2"
        style={{ height: "40px", overflow: "hidden" }}
      >
        <table style={{ width: "97%" }}>
          <thead style={{ position: "sticky", top: "0", zIndex: 1 }}>
            <tr>
              <th
                className="position-relative"
                style={{ color: theme === "light" ? "black" : "" }}
              >
                Amount
                <InfoIcon
                  style={{
                    color: "#a8a1a1",
                    cursor: "pointer",
                    margin: "3px",
                    position: "absolute",
                    marginTop: "2px",
                    fontSize: "16px",
                  }}
                  onClick={() => setShow(true)}
                />
              </th>
              <th style={{ color: theme === "light" ? "black" : "" }}>Price</th>
              <th
                style={{
                  color: theme === "light" ? "black" : "",
                  textAlign: "center",
                }}
              >
                Total
              </th>
            </tr>
          </thead>
        </table>
      </div>

      {/* ASK TABLE */}
      <div className="table_content reverse_table" ref={tableContentRef}>
        <table style={{ width: "100%" }}>
          <tbody>
            {askdata.length ? (
              [...askdata]
                .sort((a, b) => b.price - a.price)
                .map((d) => (
                  <tr
                    key={`${d.price}-${d.amount}-ask`}
                    className={d.newly ? "bg-flash-red" : ""}
                  >
                    <td className="text-start">{getAmount(d)}</td>
                    <td
                      style={{ color: "#F5475D" }}
                      className="text-end"
                      onClick={() => handleSellClick(getPrice(d))}
                    >
                      {getPrice(d)}
                    </td>
                    <td className="text-end">
                      {(getAmount(d) * getPrice(d)).toFixed(6)}
                    </td>
                  </tr>
                ))
            ) : (
              <tr>
                <td colSpan={3}>No Data</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* HIGH/LOW PRICE */}
      <div
        className={containerClass}
        style={{ backgroundColor: "var(--body-color-less)" }}
      >
        {currencySymbols[basecurrency?.toUpperCase()] && (
          <span style={{ color: textColor }}>
            {currencySymbols[basecurrency.toUpperCase()]}
          </span>
        )}
        <span style={{ color: textColor, paddingRight: "5px" }}>
          {chartData?.ctp}
        </span>
        <span className="font-sm ml-2" id="pricegreen">
          {toggle ? (
            <span style={{ fontSize: "10px", color: "#21CB80" }}>
              <ArrowUpwardIcon sx={{ fontSize: "12px" }} /> 24H H: {highPrice}
            </span>
          ) : (
            <span style={{ fontSize: "10px", color: "#21CB80" }}>
              <ArrowDownwardIcon sx={{ fontSize: "12px" }} /> 24H L: {lowPrice}
            </span>
          )}
        </span>
      </div>

      {/* BID TABLE */}
      <div className="table_content tour3">
        <table style={{ width: "100%", marginTop: "25px" }}>
          <tbody>
            {biddata.length ? (
              [...biddata]
                .sort((a, b) => b.price - a.price)
                .map((d) => (
                  <tr
                    key={`${d.price}-${d.amount}-bid`}
                    className={d.newly ? "bg-flash-green" : ""}
                  >
                    <td className="text-start">{getAmount(d)}</td>
                    <td
                      style={{ color: "#21CB80" }}
                      className="text-end"
                      onClick={() => handleBuyClick(getPrice(d))}
                    >
                      {getPrice(d)}
                    </td>
                    <td className="text-end">
                      {(getAmount(d) * getPrice(d)).toFixed(6)}
                    </td>
                  </tr>
                ))
            ) : (
              <tr>
                <td colSpan={3}>No Data</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <InfoPopUp
        open={show}
        onClose={handleClose}
        title={title}
        description={description}
      />
    </div>
  );
};

export default OrderBook;
